import React from "react";
import { motion } from "framer-motion";

/* Full-screen branded preloader: hex ring draws in, hive emblem pops, wordmark reveals, then curtain lifts */
const Preloader = () => (
  <motion.div
    key="preloader"
    data-testid="preloader"
    className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#FDFBF7]"
    exit={{ y: "-100%", transition: { duration: 0.7, ease: [0.76, 0, 0.24, 1] } }}
  >
    {/* subtle honeycomb backdrop */}
    <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.05]" aria-hidden="true">
      <defs>
        <pattern id="pre-hexes" width="56" height="97" patternUnits="userSpaceOnUse" patternTransform="scale(1.1)">
          <path
            d="M28 0 L56 16 L56 48 L28 64 L0 48 L0 16 Z M28 64 L56 80 L56 112 L28 128 L0 112 L0 80 Z"
            fill="none"
            stroke="#E8930C"
            strokeWidth="1"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#pre-hexes)" />
    </svg>

    <div className="relative flex h-56 w-56 items-center justify-center">
      {/* pulsing glow */}
      <motion.div
        className="absolute h-44 w-44 rounded-full"
        style={{ background: "radial-gradient(circle, rgba(240,163,34,0.25), transparent 65%)" }}
        animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.9, 0.5] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* hexagon stroke drawing itself */}
      <svg className="absolute" width="220" height="220" viewBox="0 0 220 220" fill="none">
        <motion.path
          d="M110 14 L196 62 L196 158 L110 206 L24 158 L24 62 Z"
          stroke="#E8930C"
          strokeWidth="2.5"
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 1.1, ease: "easeInOut" }}
        />
        <motion.path
          d="M110 34 L179 72 L179 148 L110 186 L41 148 L41 72 Z"
          stroke="#F0A322"
          strokeOpacity="0.35"
          strokeWidth="1.5"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.1, delay: 0.2, ease: "easeInOut" }}
        />
      </svg>

      {/* hive emblem pop */}
      <motion.img
        src="/assets/logo-hex-hd.png"
        alt="MedHive"
        className="relative w-24 drop-shadow-[0_10px_30px_rgba(232,147,12,0.35)]"
        initial={{ scale: 0, rotate: -20, opacity: 0 }}
        animate={{ scale: [0, 1.12, 1], rotate: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.55, ease: [0.34, 1.56, 0.64, 1] }}
      />
    </div>

    {/* wordmark letter reveal */}
    <div className="mt-2 flex overflow-hidden">
      {"MedHive".split("").map((ch, i) => (
        <motion.span
          key={i}
          className="font-heading text-3xl font-bold tracking-tight text-[#1F1B13]"
          initial={{ y: "120%", opacity: 0 }}
          animate={{ y: "0%", opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.9 + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
        >
          {ch}
        </motion.span>
      ))}
    </div>

    {/* loading bar */}
    <div className="mt-6 h-[3px] w-40 overflow-hidden rounded-full bg-[#F0A322]/15">
      <motion.div
        className="h-full rounded-full bg-[#E8930C]"
        initial={{ width: "0%" }}
        animate={{ width: "100%" }}
        transition={{ duration: 1.9, ease: "easeInOut" }}
      />
    </div>
  </motion.div>
);

export default Preloader;
